import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import OrderReview from '../../features/order-review/OrderReview';
import { selectCartItemsQuantity } from '../../selectors/cart';
import { selectIsCarPresent } from '../../selectors/message';
import Colors from '../../utils/color';
import ConsoleCard from './ConsoleCard';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  count: {
    color: Colors.silverChalice,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  // Scrolls the cart line items internally so the scrollbar stays inside the
  // card, not on the outer resizable panel.
  itemsScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: spacing(0.5),
  },
}));

interface AgentCartCardProps {
  // Controlled collapse so the parent drives the resizable panel; falls back to
  // internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * AGENT CART card (right pane, top). The working cart's line items + modifiers
 * (OrderReview) under an "Agent Cart" heading with a live item count. The totals
 * and Cancel / Finish actions are NOT here — they are pinned in the RightSidebar
 * footer so they stay visible however the cards are resized or collapsed.
 */
const AgentCartCard = ({
  open: openProp,
  onOpenChange,
}: AgentCartCardProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const itemQuantities = useAppSelector(selectCartItemsQuantity);
  const isCarPresent = useAppSelector(selectIsCarPresent);

  // selectCartItemsQuantity is a per-item map; total = sum of the quantities.
  const itemCount = React.useMemo(
    () =>
      Object.values(itemQuantities ?? {}).reduce(
        (sum, qty) => sum + (qty || 0),
        0
      ),
    [itemQuantities]
  );

  return (
    <ConsoleCard
      icon={<ShoppingCartOutlinedIcon />}
      title="Agent Cart"
      open={open}
      onToggle={toggleOpen}
      testId="agent-cart-card"
      // OrderReview is heavy and its effects must keep syncing the order even
      // while collapsed — keep it mounted (hidden) so toggling is instant.
      keepBodyMounted
      headerRight={
        isCarPresent ? (
          <span className={classes.count}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        ) : undefined
      }
    >
      <div id="agent-cart" className={classes.itemsScroll}>
        <OrderReview />
      </div>
    </ConsoleCard>
  );
};

export default AgentCartCard;
